import React from 'react'
import {
  Link,
  Outlet,
  createFileRoute,
  useLocation,
} from '@tanstack/react-router'
import {
  Activity,
  BarChart3,
  Bell,
  Check,
  FileText,
  Gauge,
  Globe,
  LogOut,
  MapPin,
  Moon,
  Server,
  Settings as SettingsIcon,
  Shield,
  Sun,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useTheme } from '@/lib/theme'
import { useAuth } from '@/lib/auth-oidc'
import { useLanguage } from '@/lib/language'
import { useVersion } from '@/lib/api/queries'
import { localizeDigits } from '@/lib/localize-digits'
import { useFeatureFlag } from '@/lib/use-feature-flags'
import { Switch } from '@/components/ui/switch'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export const Route = createFileRoute('/settings')({
  component: SettingsLayout,
})

function SettingsLayout() {
  const location = useLocation()

  // If we're on a child route (like /settings/advanced), render the outlet
  // Otherwise render the main settings page
  if (location.pathname !== '/settings') {
    return <Outlet />
  }

  return <SettingsPage />
}

function SettingsPage() {
  const { theme, setTheme, resolvedTheme } = useTheme()
  const { user, logout } = useAuth()
  const { t } = useTranslation()
  const { language, setLanguage } = useLanguage()
  const { data: versionData, isLoading: versionLoading } = useVersion()
  const [favoriteLocationsEnabled, setFavoriteLocationsEnabled] =
    useFeatureFlag('favoriteLocations')
  const [analyticsEnabled, setAnalyticsEnabled] = useFeatureFlag('analytics')
  const [showLogoutDialog, setShowLogoutDialog] = React.useState(false)
  const [showLanguageDialog, setShowLanguageDialog] = React.useState(false)

  const toggleTheme = () => {
    if (theme === 'light') {
      setTheme('dark')
    } else if (theme === 'dark') {
      setTheme('system')
    } else {
      setTheme('light')
    }
  }

  const getThemeLabel = () => {
    if (theme === 'system') {
      return t('settings.appearance.system')
    }
    return theme === 'light'
      ? t('settings.appearance.light')
      : t('settings.appearance.dark')
  }

  const getLanguageName = () => {
    return language === 'en'
      ? t('settings.general.english')
      : t('settings.general.persian')
  }

  const handleLogout = () => {
    logout()
    setShowLogoutDialog(false)
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <div>
      {/* Fixed Header */}
      <div className="fixed-header">
        <div className="container mx-auto max-w-screen-md px-4 py-4">
          <h1 className="text-2xl font-bold">{t('settings.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('settings.description')}
          </p>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-nav-and-safe-area">
        <div className="space-y-4 pt-4">
          {/* User Account Section */}
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.account.title')}</CardTitle>
              <CardDescription>
                {t('settings.account.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">
                      {user ? getInitials(user.name ?? user.account_name) : 'U'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium">
                      {user?.name ??
                        user?.account_name ??
                        t('settings.account.guest')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {user?.email ?? t('settings.account.notLoggedIn')}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowLogoutDialog(true)}
                >
                  <LogOut className="h-4 w-4 me-2" />
                  {t('settings.account.logout')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Appearance Section */}
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.appearance.title')}</CardTitle>
              <CardDescription>
                {t('settings.appearance.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {resolvedTheme === 'dark' ? (
                    <Moon className="h-5 w-5 text-muted-foreground" />
                  ) : (
                    <Sun className="h-5 w-5 text-muted-foreground" />
                  )}
                  <div>
                    <p className="text-sm font-medium">
                      {t('settings.appearance.theme')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {getThemeLabel()} {t('settings.appearance.mode')}
                    </p>
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={toggleTheme}>
                  {resolvedTheme === 'dark' ? (
                    <Sun className="h-4 w-4 me-2" />
                  ) : (
                    <Moon className="h-4 w-4 me-2" />
                  )}
                  {t('settings.appearance.switch')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* General Section */}
          <Card>
            <CardHeader>
              <CardTitle>{t('settings.general.title')}</CardTitle>
              <CardDescription>
                {t('settings.general.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {t('settings.general.language')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {getLanguageName()}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowLanguageDialog(true)}
                >
                  {t('settings.general.change')}
                </Button>
              </div>
              {favoriteLocationsEnabled && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">
                        {t('favoriteLocations.title')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('favoriteLocations.description')}
                      </p>
                    </div>
                  </div>
                  <Link to="/settings/locations">
                    <Button variant="outline" size="sm">
                      {t('settings.general.open')}
                    </Button>
                  </Link>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <SettingsIcon className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {t('settings.general.advancedFeatures')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('settings.general.advancedDescription')}
                    </p>
                  </div>
                </div>
                <Link to="/settings/advanced">
                  <Button variant="outline" size="sm">
                    {t('settings.general.open')}
                  </Button>
                </Link>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bell className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {t('notificationSettings.title')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('notificationSettings.description')}
                    </p>
                  </div>
                </div>
                <Link to="/settings/notifications">
                  <Button variant="outline" size="sm">
                    {t('settings.general.open')}
                  </Button>
                </Link>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{t('policies.title')}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('policies.description')}
                    </p>
                  </div>
                </div>
                <Link to="/settings/policies">
                  <Button variant="outline" size="sm">
                    {t('settings.general.open')}
                  </Button>
                </Link>
              </div>
              {user?.is_admin && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Gauge className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{t('quotas.title')}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('quotas.settingsDescription')}
                      </p>
                    </div>
                  </div>
                  <Link to="/settings/quotas">
                    <Button variant="outline" size="sm">
                      {t('settings.general.open')}
                    </Button>
                  </Link>
                </div>
              )}
              {user?.is_admin && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Server className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">
                        {t('vehicleFetch.title')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('vehicleFetch.settingsDescription')}
                      </p>
                    </div>
                  </div>
                  <Link to="/settings/dispatcher">
                    <Button variant="outline" size="sm">
                      {t('settings.general.open')}
                    </Button>
                  </Link>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">
                      {t('auditLogs.title')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t('auditLogs.settingsDescription')}
                    </p>
                  </div>
                </div>
                <Link to="/settings/audit-logs">
                  <Button variant="outline" size="sm">
                    {t('settings.general.open')}
                  </Button>
                </Link>
              </div>
              {analyticsEnabled && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <BarChart3 className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">
                        {t('analytics.title')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('analytics.settingsDescription')}
                      </p>
                    </div>
                  </div>
                  <Link to="/settings/analytics">
                    <Button variant="outline" size="sm">
                      {t('settings.general.open')}
                    </Button>
                  </Link>
                </div>
              )}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Activity className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">{t('cache.title')}</p>
                    <p className="text-xs text-muted-foreground">
                      {t('cache.settingsDescription')}
                    </p>
                  </div>
                </div>
                <Link to="/settings/cache">
                  <Button variant="outline" size="sm">
                    {t('settings.general.open')}
                  </Button>
                </Link>
              </div>
              {favoriteLocationsEnabled && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">
                        {t('favoriteLocations.title')}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t('favoriteLocations.settingsDescription')}
                      </p>
                    </div>
                  </div>
                  <Link to="/settings/locations">
                    <Button variant="outline" size="sm">
                      {t('settings.general.open')}
                    </Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Feature Flags */}
          <Card>
            <CardHeader>
              <CardTitle>{t('featureFlags.title')}</CardTitle>
              <CardDescription>{t('featureFlags.description')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    {t('featureFlags.analytics')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('featureFlags.analyticsDescription')}
                  </p>
                </div>
                <Switch
                  checked={analyticsEnabled}
                  onCheckedChange={setAnalyticsEnabled}
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    {t('featureFlags.favoriteLocations')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {t('featureFlags.favoriteLocationsDescription')}
                  </p>
                </div>
                <Switch
                  checked={favoriteLocationsEnabled}
                  onCheckedChange={setFavoriteLocationsEnabled}
                />
              </div>
            </CardContent>
          </Card>

          {/* Version Information */}
          <Card className="border-dashed">
            <CardHeader>
              <CardTitle className="text-sm">
                {t('settings.version.title')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-xs">
              {/* Server Version */}
              <div>
                <p className="font-medium text-muted-foreground mb-1">
                  {t('settings.version.serverVersion')}
                </p>
                {versionLoading ? (
                  <p className="text-muted-foreground">
                    {t('settings.version.loading')}
                  </p>
                ) : versionData ? (
                  <div className="space-y-1">
                    <p className="font-mono">
                      <span className="text-muted-foreground">
                        {t('settings.version.commitSha')}:
                      </span>{' '}
                      {versionData.commit_sha.substring(0, 8)}
                    </p>
                    <p className="font-mono">
                      <span className="text-muted-foreground">
                        {t('settings.version.buildTime')}:
                      </span>{' '}
                      {localizeDigits(
                        new Date(versionData.build_time).toLocaleString(),
                      )}
                    </p>
                  </div>
                ) : (
                  <p className="text-muted-foreground">
                    {t('settings.version.error')}
                  </p>
                )}
              </div>

              {/* PWA Version */}
              <div>
                <p className="font-medium text-muted-foreground mb-1">
                  {t('settings.version.pwaVersion')}
                </p>
                <div className="space-y-1">
                  <p className="font-mono">
                    <span className="text-muted-foreground">
                      {t('settings.version.commitSha')}:
                    </span>{' '}
                    {__PWA_COMMIT_SHA__}
                  </p>
                  <p className="font-mono">
                    <span className="text-muted-foreground">
                      {t('settings.version.buildTime')}:
                    </span>{' '}
                    {localizeDigits(
                      new Date(__PWA_BUILD_TIME__).toLocaleString(),
                    )}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('settings.account.logoutTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.account.logoutDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('settings.account.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleLogout}>
              {t('settings.account.logout')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Language Selection Dialog */}
      <AlertDialog
        open={showLanguageDialog}
        onOpenChange={setShowLanguageDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('settings.general.language')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('settings.general.change')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2 py-4">
            <Button
              variant={language === 'en' ? 'default' : 'outline'}
              className="w-full justify-start"
              onClick={() => {
                setLanguage('en')
                setShowLanguageDialog(false)
              }}
            >
              {language === 'en' && <Check className="h-4 w-4 me-2" />}
              {t('settings.general.english')}
            </Button>
            <Button
              variant={language === 'fa' ? 'default' : 'outline'}
              className="w-full justify-start"
              onClick={() => {
                setLanguage('fa')
                setShowLanguageDialog(false)
              }}
            >
              {language === 'fa' && <Check className="h-4 w-4 me-2" />}
              {t('settings.general.persian')}
            </Button>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>
              {t('settings.account.cancel')}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
